import { createSerializationAdapter } from "@tanstack/react-router";

import { UserFacingError } from "~/lib/shared/user-facing-error";

/**
 * Carries a `UserFacingError` across the server-function wire as itself.
 *
 * TanStack Start's default seroval plugins include `ShallowErrorPlugin`, which
 * sends any thrown `Error` as its message alone and rebuilds it as a plain
 * `Error`: the class, the `name` and every other property are gone. Without
 * this adapter no component could tell a server-written message from a
 * database error, so each one fell back to generic "try again" copy.
 * Registered in `src/start.ts`; Start places adapters ahead of its default
 * plugins, so this one wins for every `UserFacingError`, subclasses included.
 *
 * Only `name`, `message` and a string `code` cross. `name` keeps a subclass
 * such as `ContentLifecycleError` recognisable; `code` is the machine-readable
 * reason some subclasses carry. Nothing else may: `cause` is usually the
 * database error the message was written to replace, and `stack` describes the
 * server.
 *
 * Kept apart from the class so that throwing or checking a `UserFacingError`
 * does not import the router; only `src/start.ts` and tests need this.
 */
export const userFacingErrorAdapter = createSerializationAdapter({
  key: "UserFacingError",
  test: (value): value is UserFacingError => value instanceof UserFacingError,
  toSerializable: (error) => ({
    name: error.name,
    message: error.message,
    code:
      "code" in error && typeof error.code === "string"
        ? error.code
        : undefined,
  }),
  fromSerializable: ({ name, message, code }) => {
    const error = new UserFacingError(message);
    error.name = name;
    return code === undefined ? error : Object.assign(error, { code });
  },
});
